import { useLazyQuery } from '@apollo/client';
import { useCallback, useState } from 'react';

import { getTempInfoCountryConfig } from '../constants/countries';
import { OtherLienholderSlug, isTreatedAsOther } from '../constants/lienholders';
import { ttJurisdictionDefaultConditions } from '../constants/taterTitle';
import {
  StateAbbreviation,
  TtProductType,
  useGetTtJurisdictionLazyQuery,
} from '../gql/generated/graphql';
import { lienholderQuery } from '../gql/prs/lienholderGql';
import { ResidenceFormValues } from '../pages/LoanApp/utils';
import { RudderEvent, rudderanalytics } from '../services/analytics/rudderstack';
import { getAddressValue } from '../utils/address';
import { useTempInfo } from './useTempInfo';

interface Warnings {
  directPay: boolean;
  requireWalkIn: boolean;
  doubleTax: boolean;
}

const useCheckWarnings = () => {
  const { info } = useTempInfo();
  const [ttJurisdictionQuery, { loading: jurisdictionLoading }] = useGetTtJurisdictionLazyQuery();
  const [getLienholderInfo, { loading: lienholderInfoLoading }] = useLazyQuery(lienholderQuery);

  const [warnings, setWarnings] = useState<Warnings>({
    directPay: false,
    requireWalkIn: false,
    doubleTax: false,
  });

  const checkWarnings = useCallback(
    async ({
      address_line,
      address_line_2,
      city,
      state,
      zip,
      moved_states,
    }: Partial<ResidenceFormValues>) => {
      const address = getAddressValue({
        address_line,
        address_line_2,
        city,
        state,
        zip: zip?.toString(),
      });
      const lienholderSlug = info?.data?.lienholder_slug;
      const treatedAsOther = isTreatedAsOther(info?.data);

      let directPay = false;
      let doubleTax = false;
      if (!treatedAsOther) {
        const response = await getLienholderInfo({
          fetchPolicy: 'network-only',
          variables: {
            state,
            slug: lienholderSlug,
          },
        });

        const lienholderInfo = response?.data?.lienholder;
        directPay = lienholderInfo?.requirements_to_payoff_lease?.direct_pay?.value;
        doubleTax = lienholderInfo?.double_tax;

        if (doubleTax) {
          rudderanalytics.track(RudderEvent.DoubleTaxModalShown);
        }
      }

      const jurisdiction = getTempInfoCountryConfig(info?.data).jurisdictionLookup
        ? await ttJurisdictionQuery({
            variables: {
              state: state as StateAbbreviation,
              types: [TtProductType.Registration, TtProductType.Title],
              conditions: {
                ...ttJurisdictionDefaultConditions(state as StateAbbreviation),
                // Non-core lienholders aren't in TaterTitle's Lienholder enum — treat as Other.
                lienholder: treatedAsOther ? OtherLienholderSlug : lienholderSlug,
                hasCobuyer: false,
                driversLicenseAddress: address,
                currentAddress: address,
                vehicleAge: new Date().getFullYear() - parseInt(info?.data?.year || '0', 10),
                transferringPlates: false,
                driversLicenseState: state,
                movedStateDuringLease: moved_states === true.toString(),
                jurisdictionState: state,
              },
            },
          })
        : undefined;

      const requireWalkIn = jurisdiction?.data?.jurisdiction?.requireWalkIn ?? false;

      setWarnings((prevState) => ({
        ...prevState,
        directPay,
        requireWalkIn,
        doubleTax,
      }));
    },
    [info?.data, getLienholderInfo, ttJurisdictionQuery],
  );

  const anyWarning = Object.values(warnings).some((warning) => warning);
  const loading = jurisdictionLoading || lienholderInfoLoading;

  return {
    warnings,
    checkWarnings,
    anyWarning,
    loading,
  };
};

export default useCheckWarnings;
